import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import Square from './Square';
import QuestionModal from './QuestionModal';
import { startWebPlayingPresence } from './lib/tracking';

const GRID_SIZE = 6;
const PICK_SECONDS = 30;

const shuffle = (items) => [...items].sort(() => Math.random() - 0.5);

const checkWin = (grid, playerNum) => {
  let visited = Array(GRID_SIZE).fill(0).map(() => Array(GRID_SIZE).fill(false));
  const dfsTB = (r, c) => {
    if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE || visited[r][c] || grid[r][c] !== playerNum) return false;
    if (r === GRID_SIZE - 1) return true;
    visited[r][c] = true;
    return dfsTB(r+1, c) || dfsTB(r-1, c) || dfsTB(r, c+1) || dfsTB(r, c-1);
  };

  for (let c = 0; c < GRID_SIZE; c++) {
    if (grid[0][c] === playerNum && dfsTB(0, c)) return true;
  }

  visited = Array(GRID_SIZE).fill(0).map(() => Array(GRID_SIZE).fill(false));
  const dfsLR = (r, c) => {
    if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE || visited[r][c] || grid[r][c] !== playerNum) return false;
    if (c === GRID_SIZE - 1) return true;
    visited[r][c] = true;
    return dfsLR(r+1, c) || dfsLR(r-1, c) || dfsLR(r, c+1) || dfsLR(r, c-1);
  };

  for (let r = 0; r < GRID_SIZE; r++) {
    if (grid[r][0] === playerNum && dfsLR(r, 0)) return true;
  }

  return false;
};

const getCellNum = (r, c, direction = 'rtl') => {
  if (direction === 'ltr') return r * GRID_SIZE + c + 1;
  return r * GRID_SIZE + (GRID_SIZE - c);
};

const getEmptyCells = (grid, direction = 'rtl') => {
  const cells = [];
  grid.forEach((row, r) => {
    row.forEach((cell, c) => {
      if (cell === 0) cells.push({ r, c, num: getCellNum(r, c, direction) });
    });
  });
  return cells;
};

let computerTauntQueue = [];
let lastComputerTaunt = null;

const refillComputerTauntQueue = (taunts) => {
  computerTauntQueue = shuffle(taunts);
  if (computerTauntQueue.length > 1 && computerTauntQueue[0] === lastComputerTaunt) {
    const [repeated] = computerTauntQueue.splice(0, 1);
    computerTauntQueue.push(repeated);
  }
};

const getRandomTaunt = (taunts) => {
  if (!computerTauntQueue.length) {
    refillComputerTauntQueue(taunts);
  }

  const taunt = computerTauntQueue.shift();
  lastComputerTaunt = taunt;
  return taunt;
};

const getShortestPathCost = (grid, playerNum, direction) => {
  const opponent = playerNum === 1 ? 2 : 1;
  const distances = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(Infinity));
  const queue = [];

  const getCost = (r, c) => {
    if (grid[r][c] === opponent) return 1000;
    if (grid[r][c] === playerNum) return 0;
    return 1;
  };

  const addStart = (r, c) => {
    const cost = getCost(r, c);
    distances[r][c] = cost;
    queue.push({ r, c, cost });
  };

  if (direction === 'tb') {
    for (let c = 0; c < GRID_SIZE; c++) addStart(0, c);
  } else {
    for (let r = 0; r < GRID_SIZE; r++) addStart(r, 0);
  }

  const directions = [[1, 0], [-1, 0], [0, 1], [0, -1]];

  while (queue.length) {
    queue.sort((a, b) => a.cost - b.cost);
    const current = queue.shift();
    if (current.cost !== distances[current.r][current.c]) continue;

    for (const [dr, dc] of directions) {
      const nr = current.r + dr;
      const nc = current.c + dc;
      if (nr < 0 || nr >= GRID_SIZE || nc < 0 || nc >= GRID_SIZE) continue;

      const nextCost = current.cost + getCost(nr, nc);
      if (nextCost < distances[nr][nc]) {
        distances[nr][nc] = nextCost;
        queue.push({ r: nr, c: nc, cost: nextCost });
      }
    }
  }

  if (direction === 'tb') return Math.min(...distances[GRID_SIZE - 1]);
  return Math.min(...distances.map(row => row[GRID_SIZE - 1]));
};

const getBestPathCost = (grid, playerNum) => Math.min(
  getShortestPathCost(grid, playerNum, 'tb'),
  getShortestPathCost(grid, playerNum, 'lr')
);

const canConnectInDirection = (grid, playerNum, pathDirection) => {
  const opponent = playerNum === 1 ? 2 : 1;
  const visited = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(false));
  const stack = [];
  const canPass = (r, c) => grid[r]?.[c] !== opponent;

  if (pathDirection === 'tb') {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (canPass(0, c)) stack.push({ r: 0, c });
    }
  } else {
    for (let r = 0; r < GRID_SIZE; r++) {
      if (canPass(r, 0)) stack.push({ r, c: 0 });
    }
  }

  while (stack.length) {
    const { r, c } = stack.pop();
    if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE || visited[r][c] || !canPass(r, c)) continue;
    if ((pathDirection === 'tb' && r === GRID_SIZE - 1) || (pathDirection === 'lr' && c === GRID_SIZE - 1)) return true;
    visited[r][c] = true;
    stack.push({ r: r + 1, c }, { r: r - 1, c }, { r, c: c + 1 }, { r, c: c - 1 });
  }

  return false;
};

const canStillConnect = (grid, playerNum) =>
  canConnectInDirection(grid, playerNum, 'tb') || canConnectInDirection(grid, playerNum, 'lr');

const arePathsBlocked = (grid) => !canStillConnect(grid, 1) && !canStillConnect(grid, 2);

const countOwnedCells = (grid) =>
  grid.reduce(
    (counts, row) => {
      row.forEach((cell) => {
        if (cell === 1) counts.team1 += 1;
        else if (cell === 2) counts.team2 += 1;
        else counts.empty += 1;
      });
      return counts;
    },
    { team1: 0, team2: 0, empty: 0 }
  );

const getWinnerByCellCount = (grid) => {
  const counts = countOwnedCells(grid);
  if (counts.empty > 0) return 0;
  if (counts.team1 > counts.team2) return 1;
  if (counts.team2 > counts.team1) return 2;
  return 3;
};

const chooseComputerCell = (grid, direction = 'rtl') => {
  const emptyCells = getEmptyCells(grid, direction);
  if (!emptyCells.length) return null;

  const testMove = (cell, playerNum) => {
    const nextGrid = grid.map(row => [...row]);
    nextGrid[cell.r][cell.c] = playerNum;
    return checkWin(nextGrid, playerNum);
  };

  const winningMove = emptyCells.find(cell => testMove(cell, 2));
  if (winningMove) return winningMove;

  const blockingMove = emptyCells.find(cell => testMove(cell, 1));
  if (blockingMove) return blockingMove;

  const currentAiPath = getBestPathCost(grid, 2);
  const currentHumanPath = getBestPathCost(grid, 1);

  const scoreCell = (cell) => {
    const directions = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    const ownNeighbors = directions.filter(([dr, dc]) => grid[cell.r + dr]?.[cell.c + dc] === 2).length;
    const opponentNeighbors = directions.filter(([dr, dc]) => grid[cell.r + dr]?.[cell.c + dc] === 1).length;
    const centerBonus = 6 - (Math.abs(cell.r - 2.5) + Math.abs(cell.c - 2.5));
    const nextGrid = grid.map(row => [...row]);
    nextGrid[cell.r][cell.c] = 2;
    const aiPathGain = currentAiPath - getBestPathCost(nextGrid, 2);
    const humanPathDamage = getBestPathCost(nextGrid, 1) - currentHumanPath;
    const edgeBonus = cell.c === 0 || cell.c === GRID_SIZE - 1 ? 1.5 : 0;
    return aiPathGain * 18 + humanPathDamage * 12 + ownNeighbors * 5 + opponentNeighbors * 3 + centerBonus + edgeBonus;
  };

  return [...emptyCells]
    .map(cell => ({ ...cell, score: scoreCell(cell) + Math.random() * 0.25 }))
    .sort((a, b) => b.score - a.score)[0];
};

export default function GameBoard({ teams, initialTurn, selectedCategories = ['مختلط'], selectedDifficulties = ['مختلط'], questions = [], isMuted = false, isOpenPlay = false, openPlaySeconds = 10, isComputerMode = false, text, onWin }) {
  const [grid, setGrid] = useState(Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0)));
  const [selectedCell, setSelectedCell] = useState(null);
  const [cellQuestions, setCellQuestions] = useState({});
  const questionPoolRef = useRef([]);
  const lastQuestionMetaRef = useRef({ category: null, difficulty: null });
  const categoryCycleRef = useRef([]);
  const difficultyCycleRef = useRef([]);
  const [currentTurn, setCurrentTurn] = useState(initialTurn);
  const [pickTimeLeft, setPickTimeLeft] = useState(PICK_SECONDS);
  const [computerTargetCell, setComputerTargetCell] = useState(null);
  const [computerPointerCell, setComputerPointerCell] = useState(null);
  const [computerTaunt, setComputerTaunt] = useState('');
  const [showComputerThinkingModal, setShowComputerThinkingModal] = useState(false);
  const [showComputerPickMessage, setShowComputerPickMessage] = useState(false);
  const [isScoringMode, setIsScoringMode] = useState(false);
  const bgAudioRef = useRef(null);
  const boardFullHandledRef = useRef(false);

  useEffect(() => {
    computerTauntQueue = [];
  }, [text.aiTaunts]);

  useEffect(() => {
    let stopPresence = null;
    let cancelled = false;

    startWebPlayingPresence({
      mode: isComputerMode ? 'ai' : 'local',
      team1_name: teams.team1.name || '-',
      team2_name: teams.team2.name || '-'
    })
      .then((stop) => {
        if (cancelled) {
          stop();
          return;
        }
        stopPresence = stop;
      })
      .catch((error) => console.warn('web playing presence failed:', error));

    return () => {
      cancelled = true;
      if (stopPresence) stopPresence();
    };
  }, [isComputerMode, teams.team1.name, teams.team2.name]);

  const filteredQuestions = useMemo(() => {
    const filtered = questions.filter(q => {
      const categoryOk = selectedCategories.includes('مختلط') || selectedCategories.includes(q.category);
      const difficultyOk = selectedDifficulties.includes('مختلط') || selectedDifficulties.includes(q.difficulty);
      return categoryOk && difficultyOk;
    });
    return filtered;
  }, [questions, selectedCategories, selectedDifficulties]);

  useEffect(() => {
    const audio = new Audio('/start1.mp3');
    audio.loop = true;
    bgAudioRef.current = audio;

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    if (!bgAudioRef.current) return;
    const audio = bgAudioRef.current;

    if (selectedCell || isMuted) {
      audio.pause();
    } else {
      audio.play().catch(e => console.log('Board audio blocked:', e));
    }
  }, [selectedCell, isMuted]);

  useEffect(() => {
    const pool = shuffle(filteredQuestions);
    const categories = shuffle([...new Set(pool.map(q => q.category))]);
    const difficulties = shuffle([...new Set(pool.map(q => q.difficulty))]);

    setCellQuestions({});
    questionPoolRef.current = pool;
    categoryCycleRef.current = categories;
    difficultyCycleRef.current = difficulties;
    lastQuestionMetaRef.current = { category: null, difficulty: null };
  }, [filteredQuestions]);

  const drawQuestion = useCallback(() => {
    const pool = questionPoolRef.current;
    if (!pool.length) return null;

    const isCategoryMixed = selectedCategories.includes('مختلط') || selectedCategories.length > 1;
    const isDifficultyMixed = selectedDifficulties.includes('مختلط') || selectedDifficulties.length > 1;
    const last = lastQuestionMetaRef.current;

    const pickIndex = (predicate) => pool.findIndex(predicate);
    let index = -1;

    const refillDifficultyCycle = () => {
      difficultyCycleRef.current = shuffle([...new Set(pool.map(q => q.difficulty))]);
    };
    const refillCategoryCycle = () => {
      categoryCycleRef.current = shuffle([...new Set(pool.map(q => q.category))]);
    };

    if (isDifficultyMixed && !difficultyCycleRef.current.length) refillDifficultyCycle();
    if (isCategoryMixed && !categoryCycleRef.current.length) refillCategoryCycle();

    const targetDifficulty = isDifficultyMixed ? difficultyCycleRef.current.find(d => d !== last.difficulty) || difficultyCycleRef.current[0] : null;
    const targetCategory = isCategoryMixed ? categoryCycleRef.current.find(c => c !== last.category) || categoryCycleRef.current[0] : null;

    // Keep categories and difficulty varied across the question pool.
    if (targetDifficulty && targetCategory) {
      index = pickIndex(q => q.difficulty === targetDifficulty && q.category === targetCategory);
    }
    if (index === -1 && targetDifficulty) {
      index = pickIndex(q => q.difficulty === targetDifficulty && (!isCategoryMixed || q.category !== last.category));
    }
    if (index === -1 && targetCategory) {
      index = pickIndex(q => q.category === targetCategory && (!isDifficultyMixed || q.difficulty !== last.difficulty));
    }
    if (index === -1 && isCategoryMixed && isDifficultyMixed) {
      index = pickIndex(q => q.category !== last.category && q.difficulty !== last.difficulty);
    }
    if (index === -1 && isDifficultyMixed) {
      index = pickIndex(q => q.difficulty !== last.difficulty);
    }
    if (index === -1 && isCategoryMixed) {
      index = pickIndex(q => q.category !== last.category);
    }
    if (index === -1) index = 0;

    const [next] = pool.splice(index, 1);
    questionPoolRef.current = pool;
    if (isDifficultyMixed) {
      difficultyCycleRef.current = difficultyCycleRef.current.filter(d => d !== next.difficulty);
    }
    if (isCategoryMixed) {
      categoryCycleRef.current = categoryCycleRef.current.filter(c => c !== next.category);
    }
    lastQuestionMetaRef.current = { category: next.category, difficulty: next.difficulty };
    return next;
  }, [selectedCategories, selectedDifficulties]);

  const openCell = useCallback((r, c) => {
    if (grid[r][c] !== 0) return;
    const key = `${r}-${c}`;
    const existingQuestion = cellQuestions[key];
    const question = existingQuestion || drawQuestion();
    if (!question) return;

    if (!existingQuestion) {
      setCellQuestions(prev => ({ ...prev, [key]: question }));
    }
    setComputerTargetCell(null);
    setComputerPointerCell(null);
    setShowComputerThinkingModal(false);
    setShowComputerPickMessage(false);
    setSelectedCell({ r, c, question });
  }, [cellQuestions, drawQuestion, grid]);

  const handleCellClick = (r, c) => {
    if (isComputerMode && currentTurn === 2) return;
    openCell(r, c);
  };

  const resolveCellCountWinner = useCallback((nextGrid) => {
    const result = getWinnerByCellCount(nextGrid);
    if (!result) return null;
    const counts = countOwnedCells(nextGrid);
    if (result === 3) return { isDraw: true, name: text.drawName, color: 'gold', score: counts };
    return { ...(result === 1 ? teams.team1 : teams.team2), score: counts };
  }, [teams.team1, teams.team2, text.drawName]);

  useEffect(() => {
    if (selectedCell || boardFullHandledRef.current || countOwnedCells(grid).empty > 0) return;
    const finalWinner = resolveCellCountWinner(grid);
    if (!finalWinner) return;
    boardFullHandledRef.current = true;
    setPickTimeLeft(0);
    onWin(finalWinner);
  }, [grid, onWin, resolveCellCountWinner, selectedCell]);

  useEffect(() => {
    if (!selectedCell) {
      setPickTimeLeft(countOwnedCells(grid).empty === 0 ? 0 : PICK_SECONDS);
    }
  }, [currentTurn, grid, selectedCell]);

  useEffect(() => {
    const isComputerChoosing = isComputerMode && currentTurn === 2;
    if (countOwnedCells(grid).empty === 0) return undefined;
    if (selectedCell || isComputerChoosing) return undefined;

    const timer = setInterval(() => {
      setPickTimeLeft(prev => {
        if (prev <= 1) {
          setComputerTargetCell(null);
          setComputerPointerCell(null);
          setShowComputerThinkingModal(false);
          setShowComputerPickMessage(false);
          setCurrentTurn(turn => (turn === 1 ? 2 : 1));
          return PICK_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentTurn, grid, isComputerMode, selectedCell]);

  useEffect(() => {
    if (!isComputerMode || currentTurn !== 2 || selectedCell) return undefined;

    const cell = chooseComputerCell(grid, text.dir);
    if (!cell) return undefined;

    setComputerTargetCell(cell);
    setComputerPointerCell(null);
    setComputerTaunt(getRandomTaunt(text.aiTaunts));
    setShowComputerThinkingModal(true);
    setShowComputerPickMessage(false);
    const pickMessageTimer = setTimeout(() => {
      setShowComputerPickMessage(true);
    }, 1200);
    const hideModalTimer = setTimeout(() => {
      setShowComputerThinkingModal(false);
    }, 4000);
    const pointerTimer = setTimeout(() => {
      setComputerPointerCell(cell);
    }, 4300);
    const timer = setTimeout(() => {
      openCell(cell.r, cell.c);
    }, 6200);

    return () => {
      clearTimeout(pickMessageTimer);
      clearTimeout(hideModalTimer);
      clearTimeout(pointerTimer);
      clearTimeout(timer);
    };
  }, [currentTurn, grid, isComputerMode, openCell, selectedCell, text.aiTaunts, text.dir]);

  const handleAnswer = (isCorrect) => {
    const { r, c } = selectedCell;
    const key = `${r}-${c}`;

    if (isCorrect) {
      const newGrid = grid.map(row => [...row]);
      newGrid[r][c] = currentTurn;
      setGrid(newGrid);
      const didWin = !isScoringMode && checkWin(newGrid, currentTurn);
      const shouldEnterScoringMode = !isScoringMode && !didWin && arePathsBlocked(newGrid);
      const finalWinner = resolveCellCountWinner(newGrid);

      if (didWin) {
        onWin(currentTurn === 1 ? teams.team1 : teams.team2);
        return;
      }

      if (shouldEnterScoringMode) {
        setIsScoringMode(true);
        window.alert(`${text.scoringModeTitle}\n\n${text.scoringModeMessage}`);
      }

      if (finalWinner) {
        boardFullHandledRef.current = true;
        onWin(finalWinner);
        return;
      }
    } else {
      const nextQ = drawQuestion();
      if (nextQ) {
        setCellQuestions(prev => ({ ...prev, [key]: nextQ }));
      } else {
        setCellQuestions(prev => {
          const copy = { ...prev };
          delete copy[key];
          return copy;
        });
      }
    }

    setCurrentTurn(currentTurn === 1 ? 2 : 1);
    setSelectedCell(null);
  };

  const activeTeam = currentTurn === 1 ? teams.team1 : teams.team2;
  const cellCounts = countOwnedCells(grid);
  const squaresLabel = text.dir === 'rtl' ? 'مربعات' : 'squares';

  return (
    <div className="game-board-shell w-full min-h-[calc(100vh-2rem)] flex flex-col items-center justify-center gap-3 sm:gap-4">
      <div className="w-full max-w-5xl flex flex-row items-center justify-between gap-2 lg:gap-4 glass-soft p-3 sm:p-4 rounded-2xl">
        <div className="team-turn-stack">
          <motion.div animate={{ scale: [1, 1.05, 1], textShadow: ["0px 0px 8px rgba(248,113,113,0.4)", "0px 0px 20px rgba(248,113,113,0.9)", "0px 0px 8px rgba(248,113,113,0.4)"] }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }} className={`turn-team-name turn-team-red text-base sm:text-3xl md:text-4xl font-black text-red-500 drop-shadow-xl text-center break-words ${currentTurn === 1 ? 'turn-team-active' : ''}`}>
            <div className="turn-team-label">{teams.team1.name}</div>
            {currentTurn === 1 && !selectedCell && pickTimeLeft > 0 && (
              <div className="pick-countdown mt-1 text-[10px] sm:text-sm md:text-base font-black text-emerald-950 drop-shadow-none">
                {text.pickCountdown} <span className="inline-flex min-w-8 justify-center rounded-full bg-red-100 px-2 py-0.5 text-red-700">{pickTimeLeft}</span>
              </div>
            )}
          </motion.div>
          <div className="team-square-count red-count">
            <strong>{cellCounts.team1}</strong>
            <span>{squaresLabel}</span>
          </div>
        </div>

        <div className="team-turn-stack">
          <motion.div animate={{ scale: [1, 1.05, 1], textShadow: ["0px 0px 8px rgba(96,165,250,0.4)", "0px 0px 20px rgba(96,165,250,0.9)", "0px 0px 8px rgba(96,165,250,0.4)"] }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut", delay: 0.75 }} className={`turn-team-name turn-team-blue text-base sm:text-3xl md:text-4xl font-black text-blue-400 drop-shadow-xl text-center break-words ${currentTurn === 2 ? 'turn-team-active' : ''}`}>
            <div className={`turn-team-label ${isComputerMode ? 'computer-team-label' : ''}`}>{teams.team2.name}</div>
            {currentTurn === 2 && !selectedCell && pickTimeLeft > 0 && !(isComputerMode && currentTurn === 2) && (
              <div className="pick-countdown mt-1 text-[10px] sm:text-sm md:text-base font-black text-emerald-950 drop-shadow-none">
                {text.pickCountdown} <span className="inline-flex min-w-8 justify-center rounded-full bg-blue-100 px-2 py-0.5 text-blue-700">{pickTimeLeft}</span>
              </div>
            )}
          </motion.div>
          <div className="team-square-count blue-count">
            <strong>{cellCounts.team2}</strong>
            <span>{squaresLabel}</span>
          </div>
        </div>
      </div>

      <div className="current-turn-strip px-4 py-2 rounded-full bg-white/70 border border-amber-200/50 text-sm sm:text-base text-emerald-950 shadow-sm">
        {text.currentTurn} <span className="font-black text-emerald-800">{activeTeam.name}</span>
      </div>

      {isScoringMode && (
        <div className="w-full max-w-3xl rounded-xl border border-amber-300 bg-amber-100/80 px-4 py-3 text-center shadow-sm">
          <div className="text-sm sm:text-base font-black text-amber-800">{text.scoringModeBadge}</div>
          <div className="text-xs sm:text-sm font-bold text-emerald-950/75">{text.scoringModeHelp}</div>
        </div>
      )}

      <div className="board-with-rules">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="game-grid mx-auto w-full max-w-[min(94vw,calc(100vh-12.5rem),46rem)] aspect-square grid grid-cols-6 gap-1 md:gap-2 bg-white/55 p-2 sm:p-3 rounded-2xl border border-amber-200/50 shadow-[0_24px_70px_rgba(20,49,39,0.14)]" dir="ltr">
          {grid.map((row, r) => row.map((cell, c) => (
            <Square key={`${r}-${c}`} value={cell} num={getCellNum(r, c, text.dir)} teams={teams} onClick={() => handleCellClick(r, c)} delay={(r + (GRID_SIZE - 1 - c)) * 0.05} isComputerTarget={computerPointerCell?.r === r && computerPointerCell?.c === c} />
          )))}
        </motion.div>

        <div className="rules-bottom-wrap">
          <RulesPanel text={text} />
        </div>
      </div>

      {showComputerThinkingModal && computerTargetCell && !selectedCell && (
        <motion.div
          className="fixed inset-0 z-30 flex items-center justify-center bg-emerald-950/45 px-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          dir={text.dir}
        >
          <motion.div
            initial={{ opacity: 0, y: 22, scale: 0.88 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: 'spring', bounce: 0.35, duration: 0.55 }}
            className="relative w-full max-w-md overflow-hidden rounded-3xl border-2 border-blue-200 bg-gradient-to-b from-blue-50 via-white to-amber-50 p-6 text-center shadow-[0_28px_80px_rgba(15,23,42,0.35)]"
          >
            <motion.div
              className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
            />
            <div className="mb-3 text-xs font-black text-blue-700">{text.aiThinking}</div>
            <div className="text-xl sm:text-2xl font-black leading-relaxed text-emerald-950">{computerTaunt}</div>
            {showComputerPickMessage && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="mt-4 rounded-2xl border border-blue-200 bg-blue-100/80 px-4 py-3 text-lg sm:text-xl font-black text-blue-900"
              >
                {text.aiPick} <span className="text-2xl sm:text-3xl">{computerTargetCell.num}</span>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
      
      {selectedCell && (
        <QuestionModal 
          question={selectedCell.question} 
          team={activeTeam}
          isOpenPlay={isOpenPlay}
          openPlaySeconds={openPlaySeconds}
          isComputerTurn={isComputerMode && currentTurn === 2}
          isMuted={isMuted}
          text={text}
          onAnswer={handleAnswer} 
        />
      )}
    </div>
  );
}

function RulesPanel({ text }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.35 }}
      className="w-full lg:w-64 shrink-0 bg-white/80 backdrop-blur-md p-3 sm:p-4 rounded-2xl border border-amber-200/50 shadow-[0_18px_45px_rgba(20,49,39,0.12)]"
    >
      <div className="text-sm sm:text-base font-black text-emerald-800 mb-2">{text.winRules}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-2 text-xs sm:text-sm text-emerald-950/75 leading-relaxed">
        <div className="rounded-xl bg-white/55 border border-amber-100/70 px-3 py-2">
          {text.ruleConnect}
        </div>
        <div className="rounded-xl bg-white/55 border border-amber-100/70 px-3 py-2">
          {text.ruleOrthogonal}
        </div>
        <div className="rounded-xl bg-white/55 border border-amber-100/70 px-3 py-2">
          {text.ruleCorners}
        </div>
        <div className="rounded-xl bg-white/55 border border-amber-100/70 px-3 py-2">
          {text.ruleTimeout}
        </div>
      </div>
    </motion.div>
  );
}
